"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search as SearchIcon, ShoppingCart, Menu, X, User, ChevronDown, ChevronRight, Phone, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ModeToggle } from "@/components/layout/mode-toggle";
import MegaMenu from "@/components/layout/mega-menu";
import Cart from "@/components/cart/cart";
import { useCart } from "@/hooks/use-cart";
import Search from "@/components/layout/search";
import { createClient } from "@/lib/supabase/client";
import { useSiteSettings } from "@/lib/site-settings-context";
import Image from "next/image";
import AnnouncementBar from "@/components/layout/announcement-bar";
import { Input } from "@/components/ui/input";
import MobileSearch from "./mobile-search";

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [megaMenuOpen, setMegaMenuOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const pathname = usePathname();
  const { items } = useCart();
  const { settings } = useSiteSettings();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu when path changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [pathname]);

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-background/95 backdrop-blur-md shadow-sm"
          : "bg-transparent"
      }`}
    >
      <AnnouncementBar />
      <div className="border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6">
          <div className="flex items-center justify-between h-16">
            {/* Mobile Menu Toggle - Left */}
            <div className="md:hidden w-16 flex items-center justify-start">
              <button
                className="p-2 -ml-1.5 text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              >
                {mobileMenuOpen ? (
                  <X className="h-7 w-7" />
                ) : (
                  <Menu className="h-7 w-7" />
                )}
              </button>
            </div>

            {/* Logo - Center */}
            <div className="flex-1 flex justify-center md:justify-start">
              <Link href="/" className="flex-shrink-0 flex items-center">
                <Image 
                  src="/images/about/otglogo.png"
                  alt="Ocean Tent Global"
                  className="h-12 w-auto md:h-14"
                  width={160}
                  height={48}
                  priority
                />
              </Link>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8 py-4">
              <div 
                className="relative"
                onMouseEnter={() => setMegaMenuOpen(true)}
                onMouseLeave={() => setMegaMenuOpen(false)}
              >
                <button className="flex items-center text-sm font-bold text-gray-900 hover:text-brand-navy py-2">
                  <span>Shop</span>
                  <ChevronDown className="ml-1 h-4 w-4" />
                </button>
                <AnimatePresence>
                  {megaMenuOpen && <MegaMenu />}
                </AnimatePresence>
              </div>
              <Link href="/offers" className="text-sm font-bold text-gray-900 hover:text-brand-navy py-2">
                Offers
              </Link>
              <Link href="/new-arrivals" className="text-sm font-bold text-gray-900 hover:text-brand-navy py-2">
                New Arrivals
              </Link>
              <Link href="/blog" className="text-sm font-bold text-gray-900 hover:text-brand-navy py-2">
                Blog
              </Link>
            </nav>

            {/* Desktop Action Icons */}
            <div className="hidden md:flex items-center space-x-6 ml-8 py-4">
              <Search />
              <Link 
                href="/account" 
                className="p-2 text-gray-700 hover:text-brand-navy transition-colors"
                aria-label="Account"
              >
                <User className="h-6 w-6" />
              </Link>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-10 w-10 relative text-gray-700 hover:bg-transparent hover:text-brand-navy" 
                aria-label="Cart"
                onClick={() => setCartOpen(true)}
              >
                <ShoppingCart className="h-6 w-6" />
                {items.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-[#9A2643] text-white text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center">
                    {items.length > 9 ? '9+' : items.length}
                  </span>
                )}
              </Button>
              <div className="ml-1">
                <ModeToggle size="lg" />
              </div>
            </div>

            {/* Mobile Right Side - Account and Cart */}
            <div className="md:hidden w-16 flex items-center justify-end">
              <div className="flex space-x-0.5">
                <Link 
                  href="/account"
                  className="p-1.5 text-gray-700 hover:text-brand-navy transition-colors"
                  aria-label="Account"
                >
                  <User className="h-7 w-7" />
                </Link>
                <button
                  className="p-1.5 text-gray-700 hover:text-brand-navy transition-colors relative"
                  aria-label="Cart"
                  onClick={() => setCartOpen(true)}
                >
                  <ShoppingCart className="h-7 w-7" />
                  {items.length > 0 && (
                    <span className="absolute -top-1 -right-1 bg-[#9A2643] text-white text-[11px] font-bold rounded-full h-5 w-5 flex items-center justify-center">
                      {items.length > 9 ? '9+' : items.length}
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Search - Always Visible */}
        <div className="md:hidden pb-2">
          <MobileSearch />
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="md:hidden bg-white border-t border-gray-100"
            >
              <nav className="px-4 pt-2 pb-4 space-y-1">
                <button
                  onClick={() => setMegaMenuOpen(!megaMenuOpen)}
                  className="flex items-center w-full px-4 py-2 text-lg font-semibold text-left text-gray-800 rounded-lg hover:bg-gray-50"
                >
                  <span>Shop</span>
                  <ChevronRight className={`ml-2 h-5 w-5 transition-transform ${megaMenuOpen ? 'rotate-90' : ''}`} />
                </button>
                {megaMenuOpen && (
                  <div className="pl-4">
                    <MegaMenu />
                  </div>
                )}
                <Link href="/offers" className="block px-4 py-2 rounded-lg text-lg font-semibold text-gray-800 hover:bg-gray-50">
                  Offers
                </Link>
                <Link href="/new-arrivals" className="block px-4 py-2 rounded-lg text-lg font-semibold text-gray-800 hover:bg-gray-50">
                  New Arrivals
                </Link>
                <Link href="/blog" className="block px-4 py-2 rounded-lg text-lg font-semibold text-gray-800 hover:bg-gray-50">
                  Blog
                </Link>
                <Link href="/account" className="block px-4 py-2 rounded-lg text-lg font-semibold text-gray-800 hover:bg-gray-50">
                  My Account
                </Link>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Cart drawer */}
      <AnimatePresence>
        {cartOpen && <Cart open={cartOpen} onClose={() => setCartOpen(false)} />}
      </AnimatePresence>
    </header>
  );
}